/**
 * Campana de notificaciones de la barra superior. Lee la bandeja in-app del backend (`GET /notifications`), que el
 * proceso programado llena a partir del outbox de eventos (patron outbox: el evento se guarda en la misma transaccion
 * que el cambio, asi que nada se pierde; el reparto ocurre cada 5 min, por eso una novedad puede tardar en aparecer).
 * Se refresca sola cada minuto y al volver a la pestana. Abrir una notificacion la marca como leida y lleva a su enlace.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchNotifications, markNotificationsRead, markNotificationUnread, type NotificationItem } from '../lib/queries';
import { badgeLabel, countLabel, safeLink } from '../lib/notifications';
import { waitingLabel } from '../lib/jefe';

/** Lista pura (sin datos propios): la usa la campana y se prueba sin navegador. `now` solo para pruebas. */
export function NotificationList({
  items,
  unread,
  error,
  onOpen,
  onReadAll,
  onToggleRead,
  notice,
  now,
}: {
  items: NotificationItem[];
  unread: number;
  error?: boolean;
  onOpen: (n: NotificationItem) => void;
  onReadAll: () => void;
  /** El circulo de cada fila: sin leer -> leida, leida -> sin leer. Nunca navega. */
  onToggleRead: (n: NotificationItem) => void;
  /** Aviso breve bajo el encabezado (p. ej. por que no se pudo dejar sin leer). */
  notice?: string | null;
  now?: number;
}) {
  return (
    <div role="region" aria-label="Notificaciones">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
        <strong style={{ flex: 1, fontSize: 13 }}>Notificaciones</strong>
        {unread > 0 && (
          <button className="btn btn--ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={onReadAll}>
            Marcar todas como leídas
          </button>
        )}
      </div>
      {notice && (
        <p role="status" className="muted" style={{ fontSize: 12, padding: '8px 12px', margin: 0, borderBottom: '1px solid var(--border)', background: 'var(--sunken)' }}>
          {notice}
        </p>
      )}
      {error ? (
        <p className="muted" style={{ fontSize: 12.5, padding: 12, margin: 0 }}>
          No se pudieron cargar las notificaciones.
        </p>
      ) : items.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5, padding: 12, margin: 0 }}>
          No tiene notificaciones. Las novedades pueden tardar unos minutos en aparecer.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((n) => {
            const isUnread = n.read_at === null;
            const extra = [countLabel(n.count), waitingLabel(n.updated_at, now)].filter(Boolean).join(' · ');
            return (
              <li key={n.id} style={{ borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'stretch' }}>
                <button
                  onClick={() => onOpen(n)}
                  data-unread={isUnread}
                  style={{ all: 'unset', boxSizing: 'border-box', display: 'block', flex: 1, minWidth: 0, padding: '10px 12px', cursor: 'pointer' }}
                >
                  <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span
                      aria-hidden
                      style={{ width: 8, height: 8, borderRadius: 999, marginTop: 5, flex: 'none', background: isUnread ? 'var(--accent, #2563eb)' : 'transparent' }}
                    />
                    <span style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: isUnread ? 600 : 400 }}>
                        {n.title}
                        {n.urgency === 'alta' && (
                          <>
                            {' '}
                            <span className="pill pill--warning">Urgente</span>
                          </>
                        )}
                      </span>
                      {extra && (
                        <span className="muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
                          {extra}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
                {/* Circulo interruptor (variante A): vacio = sin leer, ✓ verde = leida. Un clic alterna, sin abrir la notificacion. */}
                <button
                  className={`notif-toggle${isUnread ? '' : ' is-read'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleRead(n);
                  }}
                  aria-pressed={!isUnread}
                  aria-label={isUnread ? 'Marcar como leída, sin abrirla' : 'Marcar como no leída'}
                  title={isUnread ? 'Marcar como leída' : 'Marcar como no leída'}
                >
                  ✓
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // Borde inferior de la campana al abrir: en telefono el panel se fija a la pantalla justo debajo (ver .notif-panel).
  const [panelTop, setPanelTop] = useState(60);
  const box = useRef<HTMLDivElement>(null);

  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    refetchInterval: 60_000, // el reparto del outbox corre cada 5 min; un minuto basta y no castiga a la D1
  });
  const read = useMutation({
    mutationFn: (ids?: string[]) => markNotificationsRead(ids),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const unreadMut = useMutation({
    mutationFn: (id: string) => markNotificationUnread(id),
    onSuccess: (r) =>
      setNotice(r.changed ? null : 'Ya tiene una novedad más reciente sin leer sobre ese mismo tema; esa queda como recordatorio.'),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Cerrar con Escape o al hacer clic fuera.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const unread = q.data?.unread ?? 0;

  function openItem(n: NotificationItem) {
    if (n.read_at === null) read.mutate([n.id]);
    setOpen(false);
    const to = safeLink(n.link);
    if (to) navigate(to);
  }

  // A diferencia de openItem: no cierra el panel ni navega -- para marcar/desmarcar varias sin salir de la lista.
  function toggleRead(n: NotificationItem) {
    setNotice(null);
    if (n.read_at === null) read.mutate([n.id]);
    else unreadMut.mutate(n.id);
  }

  return (
    <div ref={box} style={{ position: 'relative', marginRight: 4 }}>
      <button
        className="btn btn--ghost"
        aria-label={unread > 0 ? `Notificaciones, ${unread} sin leer` : 'Notificaciones'}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => {
          setPanelTop(Math.round(e.currentTarget.getBoundingClientRect().bottom + 6));
          setOpen((v) => !v);
        }}
        style={{ position: 'relative', padding: '6px 8px' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              minWidth: 16,
              padding: '0 4px',
              borderRadius: 999,
              background: 'var(--critical, #dc2626)',
              color: '#fff',
              fontSize: 10.5,
              lineHeight: '16px',
              textAlign: 'center',
              fontWeight: 600,
            }}
          >
            {badgeLabel(unread)}
          </span>
        )}
      </button>
      {open && (
        <div className="notif-panel" style={{ '--notif-top': `${panelTop}px` } as React.CSSProperties}>
          <NotificationList
            items={q.data?.items ?? []}
            unread={unread}
            error={!!q.error}
            onOpen={openItem}
            onReadAll={() => read.mutate(undefined)}
            onToggleRead={toggleRead}
            notice={notice}
          />
        </div>
      )}
    </div>
  );
}
